/**
 * Plain-words definitions for unvalued reasons whose short label does not explain
 * itself. Keyed on the EXACT reason string the server sends, so a reason without an
 * entry renders as it always has.
 *
 * The keys are a contract with the server: NO_FORMATION_REASON,
 * EXCLUDED_FROM_PACKAGE_REASON and UNSCHEDULED_REASON must match the Ruby constants
 * named beside each, which Rails specs pin against this file.
 */

/** The no-formation reason (product ruling 2026-09-24): unlanded, or landed with no mapped formation. */
export const NO_FORMATION_REASON = 'No formation assigned';

/** A slot unticked in the package wizard (SlotValuationClassifier::PACKAGE_EXCLUDED_REASON). */
export const EXCLUDED_FROM_PACKAGE_REASON = 'Excluded from package';

/** A slot no pace area scheduled (Queued::SlotAllocationsBlobJob::UNSCHEDULED_REASON). */
export const UNSCHEDULED_REASON = 'Not scheduled by Development Pace Scenario';

/**
 * The definition each reason gets by default — for "No formation assigned", any well
 * the server does not say is landed (both of its sources: the unlanded inventory zone,
 * and a scheduled slot with no formation to resolve a volume source from).
 */
export const UNVALUED_REASON_DEFINITIONS: Readonly<Record<string, string>> = {
  [NO_FORMATION_REASON]:
    'This well has no formation, so it is not valued, whatever its well status. ' +
    'It stays in the package and shows on the tract page. To value it, land the ' +
    'well in a zone, then re-run the evaluation.',
};

/**
 * What the server says about a "No formation assigned" well's landing, on the row
 * that carries the reason (INS-13385). The reason is stamped for a landed well too,
 * when the scenario maps its landing interval to no formation.
 */
export interface UnvaluedReasonContext {
  /**
   * True when the well IS landed in an interval the scenario's area config reads but
   * maps to no formation; absent/false: not landed there.
   */
  landedWithoutFormation?: boolean | null;
  /** The landing's interval name, when the server has one. */
  landingInterval?: string | null;
  /**
   * The row's reasons as a list, beside the joined `unvaluedReason` (INS-12684). Absent on a row
   * baked before the list existed, which then falls back to splitting the joined string.
   */
  unvaluedReasons?: readonly string[] | null;
}

/**
 * The definition for a landed well that the scenario gives no formation.
 *
 * @param {string | null | undefined} interval - The landing interval, if known.
 * @return {string} The definition.
 */
function landedWithoutFormationDefinition(
  interval: string | null | undefined
): string {
  const landing = interval?.trim();
  const where = landing
    ? ` in ${landing}, but the type-curve scenario has no formation mapped for that interval`
    : ', but the type-curve scenario has no formation mapped for its landing interval';
  return (
    `This well is landed${where}, so it is not valued. ` +
    "To value it, map the interval to a formation in the type-curve scenario's " +
    'area config (or correct the landing), then re-run the evaluation.'
  );
}

// How the server joins a slot's reasons (EvaluationCalculation::UnvaluedReasons::SEPARATOR).
export const REASON_SEPARATOR = ', ';

/** Where a reason gets fixed, and the action label for the link. */
export interface ReasonLink {
  href: string;
  label: string;
}

/** One part of a reason, with where it gets fixed when it has a fix. */
export interface ReasonPart {
  text: string;
  link: ReasonLink | null;
}

/**
 * The definition for exactly one reason, if it has one.
 *
 * @param {string} reason - One reason.
 * @param {UnvaluedReasonContext} [context] - The row's landing verdict, if any.
 * @return {string | undefined} The definition, or undefined when none applies.
 */
function singleReasonDefinition(
  reason: string,
  context?: UnvaluedReasonContext
): string | undefined {
  if (reason === NO_FORMATION_REASON && context?.landedWithoutFormation) {
    return landedWithoutFormationDefinition(context.landingInterval);
  }
  return Object.prototype.hasOwnProperty.call(
    UNVALUED_REASON_DEFINITIONS,
    reason
  )
    ? UNVALUED_REASON_DEFINITIONS[reason]
    : undefined;
}

/**
 * The separate reasons a row carries (INS-12684): the server's list when it sends one, which no
 * splitting can improve on, since a single reason may itself contain ", ". A row without the list
 * (an evaluation calculated before it existed) is split on ", ", as it was before the list: every
 * reason this module defines or links contains no ", ", so each still comes out whole. A repeated
 * reason is listed once.
 *
 * @param {string | null | undefined} reason - The joined reason as the server sent it.
 * @param {readonly string[] | null | undefined} reasons - The server's list, when it sent one.
 * @return {string[]} The reasons; empty when there are none.
 */
export function reasonParts(
  reason: string | null | undefined,
  reasons: readonly string[] | null | undefined
): string[] {
  const listed = (reasons ?? []).map((part) => part.trim()).filter(Boolean);
  const parts =
    listed.length > 0
      ? listed
      : (reason ?? '')
          .split(REASON_SEPARATOR)
          .map((part) => part.trim())
          .filter(Boolean);
  return [...new Set(parts)];
}

/**
 * The definition for an unvalued reason, if it has one: each of the row's reasons
 * (`reasonParts`) that has a definition, in order.
 *
 * @param {string | null | undefined} reason - The reason as the server sent it.
 * @param {UnvaluedReasonContext} [context] - The row's landing verdict and reasons list, if any.
 * @return {string | undefined} The definition, or undefined when none applies.
 */
export function unvaluedReasonDefinition(
  reason: string | null | undefined,
  context?: UnvaluedReasonContext
): string | undefined {
  const definitions = reasonParts(reason, context?.unvaluedReasons)
    .map((part) => singleReasonDefinition(part, context))
    .filter((definition): definition is string => Boolean(definition));
  return definitions.length > 0 ? definitions.join(' ') : undefined;
}

/**
 * The reason followed by its definition, for text that is read rather than hovered
 * (a notification). A reason without a definition comes back unchanged.
 *
 * @param {string} reason - The reason as the server sent it.
 * @param {UnvaluedReasonContext} [context] - The row's landing verdict, if any.
 * @return {string} The reason, plus its definition when it has one.
 */
export function unvaluedReasonSentence(
  reason: string,
  context?: UnvaluedReasonContext
): string {
  const definition = unvaluedReasonDefinition(reason, context);
  return definition ? `${reason}. ${definition}` : reason;
}
